import { ChecklistItemOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { ChecklistSectionOnView } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { ProtocolOnView, InspectionOnView } from 'presentation/inspection/inspectionOnView';
import { ProtocolSectionModel } from 'shared/domain/inspection/inspectionModel';

export enum ProtocolPdfNodeType {
  item = 'item',
  section = 'section',
}

export interface ProtocolItemPdfNode {
  type: ProtocolPdfNodeType.item;
  checklistItem: ChecklistItemOnView;
  protocolItem: ProtocolOnView;
}

export interface ProtocolSectionPdfNode {
  type: ProtocolPdfNodeType.section;
  checklistSection: ChecklistSectionOnView;
  protocolSection: ProtocolSectionModel | undefined;
}

export type ProtocolPdfNode = ProtocolItemPdfNode | ProtocolSectionPdfNode;

export type FlatChecklistNode =
  | (ProtocolItemPdfNode & { indexPath: number[]; depth: number })
  | (ProtocolSectionPdfNode & { indexPath: number[]; depth: number });

function normalizeParentSectionId(
  parentSectionId: string | null | undefined
): string | null {
  return parentSectionId ?? null;
}

function isSameParentSection(
  parentSectionId: string | null | undefined,
  expectedParentSectionId: string | null | undefined
): boolean {
  return (
    normalizeParentSectionId(parentSectionId) ===
    normalizeParentSectionId(expectedParentSectionId)
  );
}

function getNodeOrder(node: ProtocolPdfNode): string {
  return node.type === ProtocolPdfNodeType.item
    ? node.checklistItem.order
    : node.checklistSection.order;
}

export function formatChecklistIndexPath(indexPath: number[]): string {
  const joined = indexPath.join('.');
  return indexPath.length === 1 ? `${joined}.` : joined;
}

export function getSubtreeItems(
  nodes: FlatChecklistNode[],
  sectionIndex: number
): ProtocolItemPdfNode[] {
  const sectionDepth = nodes[sectionIndex]?.depth ?? 0;
  const items: ProtocolItemPdfNode[] = [];

  for (const node of nodes.slice(sectionIndex + 1)) {
    if (node.depth <= sectionDepth) {
      break;
    }
    if (node.type === ProtocolPdfNodeType.item) {
      items.push(node);
    }
  }

  return items;
}

function getChecklistSiblings(
  parentSectionId: string | null | undefined,
  inspection: InspectionOnView,
  protocolItemByChecklistItemId: Map<string, ProtocolOnView>,
  protocolSectionByChecklistSectionId: Map<string | undefined, ProtocolSectionModel>
): ProtocolPdfNode[] {
  const checklist = inspection.template?.checklist ?? [];
  const sections = inspection.template?.sections ?? [];

  const itemNodes: ProtocolPdfNode[] = checklist
    .filter((checklistItem) =>
      isSameParentSection(checklistItem.parentSectionId, parentSectionId)
    )
    .flatMap((checklistItem) => {
      const protocolItem = protocolItemByChecklistItemId.get(checklistItem._id);
      if (!protocolItem) {
        return [];
      }
      return [
        {
          type: ProtocolPdfNodeType.item as const,
          checklistItem,
          protocolItem,
        },
      ];
    });

  const sectionNodes: ProtocolPdfNode[] = sections
    .filter((checklistSection) =>
      isSameParentSection(checklistSection.parentSectionId, parentSectionId)
    )
    .map((checklistSection) => ({
      type: ProtocolPdfNodeType.section as const,
      checklistSection,
      protocolSection: protocolSectionByChecklistSectionId.get(
        checklistSection._id
      ),
    }));

  return [...itemNodes, ...sectionNodes].sort((firstNode, secondNode) =>
    getNodeOrder(firstNode).localeCompare(getNodeOrder(secondNode))
  );
}

function collectNodes(
  parentSectionId: string | null | undefined,
  parentIndexPath: number[],
  inspection: InspectionOnView,
  protocolItemByChecklistItemId: Map<string, ProtocolOnView>,
  protocolSectionByChecklistSectionId: Map<string | undefined, ProtocolSectionModel>
): FlatChecklistNode[] {
  return getChecklistSiblings(
    parentSectionId,
    inspection,
    protocolItemByChecklistItemId,
    protocolSectionByChecklistSectionId
  ).flatMap((node, index) => {
    const indexPath = [...parentIndexPath, index + 1];
    const depth = parentIndexPath.length;

    if (node.type === ProtocolPdfNodeType.section) {
      return [
        { ...node, indexPath, depth },
        ...collectNodes(
          node.checklistSection._id,
          indexPath,
          inspection,
          protocolItemByChecklistItemId,
          protocolSectionByChecklistSectionId
        ),
      ];
    }

    return [{ ...node, indexPath, depth }];
  });
}

export function buildInspectionChecklistFlatNodes(
  inspection: InspectionOnView
): FlatChecklistNode[] {
  const protocol = inspection.protocol ?? [];
  const inspectionSections = inspection.sections ?? [];

  const protocolItemByChecklistItemId = new Map(
    protocol
      .filter((protocolItem) => protocolItem.templateChecklistItem?._id)
      .map((protocolItem) => [
        protocolItem.templateChecklistItem._id,
        protocolItem,
      ])
  );
  const protocolSectionByChecklistSectionId = new Map(
    inspectionSections
      .filter((protocolSection) => protocolSection.checklistSectionId)
      .map((protocolSection) => [
        protocolSection.checklistSectionId,
        protocolSection,
      ])
  );

  return collectNodes(
    undefined,
    [],
    inspection,
    protocolItemByChecklistItemId,
    protocolSectionByChecklistSectionId
  );
}
